import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, Heart, MessageCircle, UserPlus, Calendar, Zap, Image as ImageIcon, Sparkles, Plus, Users, Shield, AlertTriangle, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { optimizeMediaUrl } from '../lib/media';
import { useAuth } from '../context/AuthContext';
import { onChatRead } from '../lib/unreadBus';
import StoryRing from '../components/StoryRing';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// Q10 — Activity simplification: 4 confusing tabs collapsed into 2 clear
// surfaces. Notifications = anything that's NOT a conversation; Messages =
// DMs + group chats + plan chats. /activity URL preserved.
// V42 — Added a third 'Vibes' tab: NOT a notification surface, but a
// lightweight social feed of recent posts from people you follow. Keeps
// Notifications clean and action-oriented; Vibes becomes the engagement
// engine.
const TABS = [
  { key: 'notifications', label: 'Notifications', testId: 'notifications' },
  { key: 'messages',      label: 'Messages',      testId: 'messages' },
  { key: 'vibes',         label: 'Vibes',         testId: 'vibes' },
];

const ICONS = {
  new_follower: UserPlus,
  follow_request: UserPlus,
  like: Heart,
  comment: MessageCircle,
  comment_reply: MessageCircle,
  comment_like: Heart,
  story_reply: MessageCircle,
  story_mention: Sparkles,
  mention: Sparkles,
  join_request: UserPlus,
  plan_joined: Check,
  plan_invite: Calendar,
  request_approved: Check,
  request_declined: Zap,
  request_waitlisted: Calendar,
  plan_cancelled: Zap,
  plan_rescheduled: Calendar,
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  const d = new Date(dateStr);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Collapse consecutive same-type/actor/target items into a single summary row. */
function groupActivities(acts) {
  const groups = [];
  for (const a of acts) {
    const key = `${a.type}|${a.actor?.id || ''}|${a.vibe_id || a.plan?.id || ''}`;
    const last = groups[groups.length - 1];
    // Only group within 48h
    const fresh = a.created_at && (Date.now() - new Date(a.created_at).getTime() < 48 * 3600_000);
    if (fresh && last && last._groupKey === key && ['like', 'comment'].includes(a.type)) {
      last._count += 1;
      last._last = a.created_at;
      continue;
    }
    groups.push({ ...a, _groupKey: key, _count: 1, _last: a.created_at });
  }
  return groups;
}

function StoriesRail() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [rail, setRail] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/stories/feed');
        setRail(data || []);
        // Q6 perf — prefetch first-story media for each unviewed entry so
        // tap-to-open feels instant. Browsers cache <link rel=preload>
        // requests, so when the user opens a story the <img> in the viewer
        // hits the cache immediately. Only prefetch up to 5 rails to avoid
        // hammering the CDN.
        if (Array.isArray(data)) {
          const toPrefetch = data
            .filter(r => r.has_unviewed && r.user?.id)
            .slice(0, 5);
          toPrefetch.forEach(r => {
            api.get(`/stories/user/${r.user.id}`).then(res => {
              const first = (res.data?.stories || [])[0];
              if (first?.media_url) {
                const url = optimizeMediaUrl(first.media_url, first.media_type);
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = first.media_type === 'video' ? 'video' : 'image';
                link.href = url;
                document.head.appendChild(link);
              }
            }).catch(() => {});
          });
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex gap-3 px-4 pb-4 overflow-x-auto hide-scrollbar">
        {[1, 2, 3, 4].map(i => <div key={i} className="skeleton w-16 h-16 rounded-full flex-shrink-0" />)}
      </div>
    );
  }

  const myEntry = rail.find(r => r.is_me);
  const others = rail.filter(r => !r.is_me);

  return (
    <div className="flex gap-4 px-4 pb-4 overflow-x-auto hide-scrollbar" data-testid="stories-rail">
      <div className="flex flex-col items-center gap-1.5 flex-shrink-0" data-testid="stories-rail-me">
        <div className="relative">
          <StoryRing
            userId={currentUser?.id}
            displayName="You"
            profilePhoto={myEntry?.user?.profile_photo || currentUser?.profile_photo}
            size={64}
            hasActiveStory={!!myEntry}
            hasUnviewedStory={!!myEntry?.has_unviewed}
            clickable={!!myEntry}
            testId={myEntry ? 'rail-me-ring' : 'rail-me-empty'}
          />
          {!myEntry && (
            <button
              type="button"
              data-testid="rail-add-story"
              onClick={() => navigate('/profile', { state: { openStory: true } })}
              className="absolute -bottom-0.5 -right-0.5 bg-[#B6E600] rounded-full p-1 border-2 border-[#F7F6F2] active:scale-90"
              aria-label="Add story"
            >
              <Plus size={10} strokeWidth={3} className="text-black" />
            </button>
          )}
        </div>
        <p className="text-[10px] font-medium text-[#111111] max-w-[60px] truncate">Your story</p>
      </div>

      {others.map(r => (
        <button
          key={r.user.id}
          data-testid={`rail-user-${r.user.id}`}
          onClick={() => navigate(`/stories/${r.user.id}`)}
          className="flex flex-col items-center gap-1.5 flex-shrink-0 active:scale-95 transition-transform"
        >
          <StoryRing
            userId={r.user.id}
            displayName={r.user.display_name}
            profilePhoto={r.user.profile_photo}
            size={64}
            hasActiveStory={true}
            hasUnviewedStory={r.has_unviewed}
            clickable={false}
          />
          <p className="text-[10px] font-medium text-[#111111] max-w-[60px] truncate">
            {r.user.display_name?.split(' ')[0]}
          </p>
        </button>
      ))}
    </div>
  );
}

function ChatsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChats = useCallback(async () => {
    try {
      const { data } = await api.get('/activity/chats');
      setRows(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchChats();
    const interval = setInterval(fetchChats, 8000);
    // Refresh whenever a chat marks itself as read (user opened it).
    const offBus = onChatRead((ref) => {
      // Optimistic — zero the matching row immediately so the red badge
      // disappears before the network round-trip completes.
      if (ref?.id) {
        setRows(prev => prev.map(r => (r.id === ref.id ? { ...r, unread_count: 0 } : r)));
      }
      // Authoritative refetch to capture any new messages too.
      fetchChats();
    });
    // Refresh when the tab regains visibility (user returning from a chat).
    const onVis = () => { if (!document.hidden) fetchChats(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      offBus();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchChats]);

  // Tapping a row clears its badge immediately — even before the chat page
  // has mounted and fired the mark-read endpoint. Server reconciles next tick.
  const openChat = (row) => {
    if (row.unread_count > 0) {
      setRows(prev => prev.map(r => (r.id === row.id ? { ...r, unread_count: 0 } : r)));
    }
    navigate(row.nav_path);
  };

  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}</div>;

  if (rows.length === 0) {
    return (
      <div className="text-center py-16" data-testid="chats-empty">
        <MessageCircle size={36} className="text-[#B0B3B8] mx-auto mb-3" />
        <p className="text-sm text-[#9B9EA3]">No chats yet</p>
        <p className="text-xs text-[#B0B3B8] mt-1">Join a plan or message someone to start</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="chats-list">
      {rows.map(row => (
        <button
          key={`${row.type}-${row.id}`}
          data-testid={`chat-row-${row.type}-${row.id}`}
          onClick={() => openChat(row)}
          className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white border border-black/[0.06] active:bg-[#F1F0EA] transition-colors text-left"
        >
          <div className="flex-shrink-0 relative">
            {row.avatar ? (
              <img loading="lazy" decoding="async" src={row.avatar} alt="" className={`w-12 h-12 object-cover ${row.type === 'plan' || row.type === 'group' ? 'rounded-2xl' : 'rounded-full'}`} />
            ) : (
              <div className={`w-12 h-12 bg-[#F1F0EA] flex items-center justify-center text-sm font-bold text-[#9B9EA3] ${row.type === 'plan' || row.type === 'group' ? 'rounded-2xl' : 'rounded-full'}`}>
                {row.type === 'plan' ? <ImageIcon size={18} /> : row.type === 'group' ? <Users size={18} /> : getInitials(row.title)}
              </div>
            )}
            {row.type === 'plan' && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-[#B6E600] rounded-full border-2 border-white flex items-center justify-center">
                <Calendar size={8} strokeWidth={3} className="text-[#111111]" />
              </span>
            )}
            {row.type === 'group' && (
              <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-[#B6E600] rounded-full border-2 border-white flex items-center justify-center">
                <Users size={8} strokeWidth={3} className="text-[#111111]" />
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-bold text-[#111111] truncate">{row.title}</p>
              <span className="text-[10px] text-[#9B9EA3] flex-shrink-0">{timeAgo(row.last_message_at)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <p className="text-xs text-[#6B6E73] truncate">
                {row.type === 'plan' && row.sender_name ? `${row.sender_name}: ` : ''}{row.last_message || '—'}
              </p>
              {row.unread_count > 0 && (
                <span data-testid={`chat-unread-${row.id}`} className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
                  {row.unread_count > 99 ? '99+' : row.unread_count}
                </span>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function UpdatesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/activity/updates?days=3');
        setItems(data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>;

  if (items.length === 0) {
    return (
      <div className="text-center py-14" data-testid="updates-empty">
        <Sparkles size={34} className="text-[#B0B3B8] mx-auto mb-3" />
        <p className="text-sm text-[#9B9EA3]">No new updates from people you follow</p>
        <button
          onClick={() => navigate('/explore')}
          className="mt-4 text-xs font-semibold text-[#3F5200] bg-[#B6E600]/15 px-4 py-2 rounded-full"
        >
          Explore people
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="updates-list">
      {items.map(it => {
        const user = it.user || {};
        const actionLabel = it.kind === 'vibe' ? 'posted a vibe' : it.kind === 'plan' ? 'is hosting a new plan' : 'added to their story';
        return (
          <button
            key={`${it.kind}-${it.id}`}
            data-testid={`update-${it.kind}-${it.id}`}
            onClick={() => navigate(it.nav_path)}
            className="w-full flex items-start gap-3 p-3 rounded-2xl bg-white border border-black/[0.06] active:bg-[#F1F0EA] transition-colors text-left"
          >
            <div className="flex-shrink-0">
              {user.profile_photo ? (
                <img loading="lazy" decoding="async" src={user.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
                  {getInitials(user.display_name)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#111111]">
                <span className="font-semibold">{user.display_name}</span>
                <span className="text-[#6B6E73]"> {actionLabel}</span>
              </p>
              {it.kind === 'vibe' && it.text && (
                <p className="text-xs text-[#6B6E73] mt-0.5 line-clamp-2">{it.text}</p>
              )}
              {it.kind === 'plan' && (
                <p className="text-xs text-[#6B6E73] mt-0.5 truncate"><span className="font-medium text-[#111111]">{it.title}</span> · {it.city}</p>
              )}
              <p className="text-[10px] text-[#9B9EA3] mt-1">{timeAgo(it.created_at)}</p>
            </div>
            {it.kind === 'vibe' && it.image && (
              <img loading="lazy" decoding="async" src={it.image} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
            )}
            {it.kind === 'plan' && it.cover_image && (
              <img loading="lazy" decoding="async" src={it.cover_image} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
            )}
            {it.kind === 'story' && it.media_url && it.media_type === 'image' && (
              <img loading="lazy" decoding="async" src={it.media_url} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0 ring-2 ring-[#B6E600]" />
            )}
            {it.kind === 'story' && it.media_type === 'video' && (
              <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center ring-2 ring-[#B6E600] flex-shrink-0">
                <ImageIcon size={16} className="text-white" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  SafetyCard — official Trust & Safety notification card             */
/* ------------------------------------------------------------------ */

function SafetyCard({ act }) {
  const safety = act.safety || {};
  const isSuspension = act.type === 'safety_suspension';
  const Icon = isSuspension ? Ban : AlertTriangle;
  const headerLabel = isSuspension ? 'Account suspended' : 'Warning issued';
  const accent = isSuspension ? '#FF3B30' : '#FF9500';

  return (
    <div
      data-testid={`safety-card-${act.id}`}
      className="rounded-2xl overflow-hidden border-2 animate-fade-in"
      style={{ borderColor: accent }}
    >
      <div
        className="px-4 py-2.5 flex items-center gap-2"
        style={{ background: accent }}
      >
        <Shield size={14} className="text-white" strokeWidth={2.6} />
        <p className="text-[11px] font-extrabold text-white uppercase tracking-wider">
          ASAP Trust &amp; Safety · {headerLabel}
        </p>
      </div>
      <div className="bg-white p-4 space-y-3">
        <div className="flex items-start gap-2.5">
          <Icon size={16} className="flex-shrink-0 mt-0.5" color={accent} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-[#111111]">
              {isSuspension
                ? 'Your account has been suspended'
                : 'You received a warning from our Trust & Safety team'}
            </p>
            {safety.target_summary && (
              <p className="text-[11px] text-[#9B9EA3] mt-0.5">
                Related to: <span className="font-semibold text-[#4B4F55]">{safety.target_summary}</span>
              </p>
            )}
          </div>
        </div>

        <Block label="Guideline" body={safety.guideline} />
        {safety.reason && <Block label="Action note" body={safety.reason} />}
        <Block
          label="What happens next"
          body={safety.next_consequence}
          accent={isSuspension ? '#FF3B30' : '#3F5200'}
        />

        <p className="text-[10.5px] text-[#9B9EA3] pt-1 border-t border-black/[0.05]">
          Issued {timeAgo(act.created_at)} · Appeals: contact ASAP support
        </p>
      </div>
    </div>
  );
}

// Smart "what's next" strip — Follow Back when I don't yet follow them,
// View Profile otherwise. Resolves the follow relation once on mount so the
// list scroll stays snappy.
function ActivityActionStrip({ act, testIdPrefix }) {
  const navigate = useNavigate();
  const [followStatus, setFollowStatus] = useState(null); // 'active' | 'pending' | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!act.actor?.id) return;
      try {
        const { data } = await api.get(`/users/${act.actor.id}`);
        if (!cancelled) setFollowStatus(data.is_following || null);
      } catch { /* leave null → show Follow Back */ }
    })();
    return () => { cancelled = true; };
  }, [act.actor?.id]);

  const handleFollowBack = async (e) => {
    e.stopPropagation();
    if (busy || !act.actor?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${act.actor.id}/follow`);
      setFollowStatus(data.status || 'active');
    } catch { /* no-op */ }
    setBusy(false);
  };

  const onProfile = (e) => {
    e.stopPropagation();
    if (act.actor?.id) navigate(`/user/${act.actor.id}`);
  };

  // If I already follow them (or have a pending request out), surface only
  // "View Profile" — keeps the UI clean per Q13/Q2a preference.
  if (followStatus === 'active' || followStatus === 'pending') {
    return (
      <div className="flex gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
        <button
          data-testid={`${testIdPrefix}-view-profile`}
          onClick={onProfile}
          className="text-[11px] font-extrabold text-[#111] bg-[#F1F0EA] px-3 py-1.5 rounded-full active:scale-95"
        >
          View profile
        </button>
      </div>
    );
  }
  return (
    <div className="flex gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
      <button
        data-testid={`${testIdPrefix}-follow-back`}
        onClick={handleFollowBack}
        disabled={busy}
        className="text-[11px] font-extrabold text-[#111] bg-[#B6E600] px-3 py-1.5 rounded-full active:scale-95 disabled:opacity-60"
      >
        {busy ? 'Following…' : 'Follow back'}
      </button>
      <button
        data-testid={`${testIdPrefix}-view-profile`}
        onClick={onProfile}
        className="text-[11px] font-extrabold text-[#111] bg-[#F1F0EA] px-3 py-1.5 rounded-full active:scale-95"
      >
        View profile
      </button>
    </div>
  );
}

function Block({ label, body, accent }) {
  if (!body) return null;
  return (
    <div className="bg-[#FAF9F4] rounded-xl px-3 py-2">
      <p className="text-[10px] font-bold text-[#9B9EA3] uppercase tracking-wider mb-0.5">{label}</p>
      <p className="text-[12.5px] leading-relaxed" style={{ color: accent || '#111111' }}>{body}</p>
    </div>
  );
}

// ── Follow-request card — Approve / Decline inline ─────────────────
function FollowRequestCard({ act, onResolved }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [resolved, setResolved] = useState(null); // 'approved' | 'declined'

  const handleApprove = async () => {
    if (busy || !act.actor?.id) return;
    setBusy(true);
    try {
      await api.post(`/users/me/follow-requests/${act.actor.id}/approve`);
      setResolved('approved');
      setTimeout(() => onResolved?.(act.actor.id), 1200);
    } catch (e) { /* eslint-disable-line no-empty */ }
    setBusy(false);
  };
  const handleDecline = async () => {
    if (busy || !act.actor?.id) return;
    setBusy(true);
    try {
      await api.post(`/users/me/follow-requests/${act.actor.id}/decline`);
      setResolved('declined');
      setTimeout(() => onResolved?.(act.actor.id), 1200);
    } catch (e) { /* eslint-disable-line no-empty */ }
    setBusy(false);
  };

  return (
    <div
      data-testid={`follow-request-card-${act.id}`}
      className="flex items-start gap-3 p-3 rounded-2xl bg-white border border-[#B6E600]/30 animate-fade-in"
    >
      <button
        onClick={() => act.actor?.id && navigate(`/user/${act.actor.id}`)}
        className="flex-shrink-0"
        aria-label="Open profile"
      >
        {act.actor?.profile_photo ? (
          <img loading="lazy" decoding="async" src={act.actor.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
            {getInitials(act.actor?.display_name)}
          </div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#2A2A2A] leading-snug">
          <span className="font-bold">{act.actor?.display_name || 'Someone'}</span>{' '}
          <span className="text-[#6B6E73]">wants to follow you</span>
        </p>
        <span className="text-[11px] text-[#9B9EA3]">{timeAgo(act.created_at)}</span>
        {!resolved ? (
          <div className="flex items-center gap-1.5 mt-2">
            <button
              data-testid={`follow-request-approve-${act.id}`}
              onClick={handleApprove}
              disabled={busy}
              className="px-3 py-1.5 rounded-full bg-[#B6E600] text-[#111] text-xs font-extrabold active:scale-95 disabled:opacity-50"
            >
              Approve
            </button>
            <button
              data-testid={`follow-request-decline-${act.id}`}
              onClick={handleDecline}
              disabled={busy}
              className="px-3 py-1.5 rounded-full bg-[#F1F0EA] text-[#111] text-xs font-extrabold active:scale-95 disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        ) : (
          <p
            data-testid={`follow-request-resolved-${act.id}`}
            className="mt-2 text-[11px] font-bold text-[#3F5200]"
          >
            {resolved === 'approved' ? '✓ Approved' : 'Declined'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * FollowingVibesFeed — V42 Activity "Vibes" tab.
 *
 * A lightweight social surface (NOT a notification list) of the latest
 * vibes from people the current user follows. Quick like/comment/open
 * actions inline, infinite scroll, pull-to-refresh via the IntersectionObserver
 * sentinel at the bottom of the list.
 *
 * Intentional choices:
 * - Renders text + ONE image (if present) — full Vibe interactions live on
 *   /vibe/:id. Keeps this list scannable.
 * - "Comment" tap navigates to /vibe/:id?reply=1 → composer auto-focuses
 *   (reusing the V40 deep-link flow). One tap to engage.
 * - Optimistic like toggle so the feed feels instant on tap.
 */
function FollowingVibesFeed() {
  const navigate = useNavigate();
  const [vibes, setVibes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const PAGE = 10;

  const fetchPage = async (offset, replace = false) => {
    try {
      const { data } = await api.get(`/vibes/following?limit=${PAGE}&offset=${offset}`);
      setHasMore(!!data.has_more);
      setVibes(prev => replace ? data.vibes : [...prev, ...data.vibes]);
    } catch {
      // Surface as empty state — error noise here would be more confusing than helpful
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchPage(0, true);
      setLoading(false);
    })();
  }, []);

  // Infinite scroll — load the next page when the sentinel enters view
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore || loading) return;
    const obs = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        setLoadingMore(true);
        await fetchPage(vibes.length);
        setLoadingMore(false);
      }
    }, { rootMargin: '200px' });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, vibes.length, loading]);

  const toggleLike = async (vibe, e) => {
    e.stopPropagation();
    const willLike = !vibe.is_liked;
    setVibes(prev => prev.map(v => v.id === vibe.id ? {
      ...v,
      is_liked: willLike,
      likes_count: Math.max(0, (v.likes_count || 0) + (willLike ? 1 : -1)),
    } : v));
    try {
      if (willLike) await api.post(`/vibes/${vibe.id}/like`);
      else await api.delete(`/vibes/${vibe.id}/like`);
    } catch {
      // Revert optimistic update on failure
      setVibes(prev => prev.map(v => v.id === vibe.id ? {
        ...v,
        is_liked: !willLike,
        likes_count: Math.max(0, (v.likes_count || 0) + (willLike ? -1 : 1)),
      } : v));
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="vibes-tab-loading">
        {[1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-2xl" />)}
      </div>
    );
  }

  if (vibes.length === 0) {
    return (
      <div className="text-center py-16 px-6" data-testid="vibes-tab-empty">
        <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
          <Zap size={26} className="text-[#3F5200]" />
        </div>
        <p className="text-base font-extrabold text-[#111] mb-1">No vibes yet</p>
        <p className="text-sm text-[#6B6E73] leading-relaxed mb-4 max-w-xs mx-auto">
          Follow people to see their latest vibes show up here, ready for a like or reply.
        </p>
        <button
          data-testid="vibes-tab-find-people"
          onClick={() => navigate('/explore')}
          className="px-5 py-2.5 rounded-full bg-[#B6E600] text-[#111] text-sm font-extrabold active:scale-95"
        >
          Find people to follow
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="vibes-tab-feed">
      {vibes.map(v => (
        <article
          key={v.id}
          data-testid={`feed-vibe-${v.id}`}
          onClick={() => navigate(`/vibe/${v.id}`)}
          className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden cursor-pointer active:bg-[#FAFAF7] transition-colors"
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-3.5 pt-3">
            <button
              onClick={(e) => { e.stopPropagation(); v.user?.id && navigate(`/user/${v.user.id}`); }}
              className="flex-shrink-0"
              aria-label="Open author"
            >
              {v.user?.profile_photo ? (
                <img loading="lazy" decoding="async" src={optimizeMediaUrl(v.user.profile_photo, { w: 80 })}
                     alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[10px] font-bold text-[#9B9EA3]">
                  {getInitials(v.user?.display_name)}
                </div>
              )}
            </button>
            <button
              type="button"
              data-testid={`feed-vibe-author-${v.id}`}
              onClick={(e) => { e.stopPropagation(); v.user?.id && navigate(`/user/${v.user.id}`); }}
              className="flex-1 min-w-0 text-left"
            >
              <p className="text-[13px] font-bold text-[#111] leading-tight truncate">{v.user?.display_name || 'Someone'}</p>
              <p className="text-[11px] text-[#9B9EA3] leading-tight">@{v.user?.username || ''} · {timeAgo(v.created_at)}</p>
            </button>
          </div>

          {/* Body */}
          {v.text && (
            <p className="px-3.5 pt-2 text-[14px] text-[#2A2A2A] leading-relaxed whitespace-pre-wrap break-words">
              {v.text.length > 240 ? v.text.slice(0, 240) + '…' : v.text}
            </p>
          )}
          {v.image && (
            <div className="mt-2.5 bg-[#F1F0EA]">
              <img
                loading="lazy" decoding="async"
                src={optimizeMediaUrl(v.image, { w: 800 })}
                alt=""
                className="w-full max-h-[360px] object-cover"
              />
            </div>
          )}

          {/* Quick actions */}
          <div className="flex items-center gap-1 px-2 py-1.5 mt-1" onClick={e => e.stopPropagation()}>
            <button
              data-testid={`feed-vibe-like-${v.id}`}
              onClick={(e) => toggleLike(v, e)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full active:scale-95 transition-colors ${
                v.is_liked ? 'text-[#FF3B30]' : 'text-[#6B6E73] hover:text-[#111]'
              }`}
            >
              <Heart size={16} fill={v.is_liked ? 'currentColor' : 'none'} strokeWidth={2.2} />
              <span className="text-[12px] font-bold tabular-nums">{v.likes_count || 0}</span>
            </button>
            <button
              data-testid={`feed-vibe-comment-${v.id}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/vibe/${v.id}?focus=composer`); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#6B6E73] hover:text-[#111] active:scale-95"
            >
              <MessageCircle size={16} strokeWidth={2.2} />
              <span className="text-[12px] font-bold tabular-nums">{v.comments_count || 0}</span>
            </button>
            <button
              data-testid={`feed-vibe-open-${v.id}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/vibe/${v.id}`); }}
              className="ml-auto text-[11px] font-extrabold text-[#3F5200] px-3 py-1.5 rounded-full hover:underline"
            >
              Open
            </button>
          </div>
        </article>
      ))}

      {/* Infinite-scroll sentinel + spinner */}
      <div ref={sentinelRef} className="py-3 text-center">
        {loadingMore && <p className="text-[11px] text-[#9B9EA3] font-bold">Loading more…</p>}
      </div>
    </div>
  );
}

export default function Activity() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('notifications');
  const [activities, setActivities] = useState([]);
  const [allChatRows, setAllChatRows] = useState([]); // merged into 'all' view
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Messages and Vibes tabs manage their own fetching.
    if (tab === 'messages' || tab === 'vibes') return;
    (async () => {
      setLoading(true);
      try {
        // Fetch ALL activity rows (the previous Inbox/Updates split was
        // confusing testers — single Notifications surface keeps it simple).
        // We don't merge chats here anymore because chats live in their own tab.
        const activityRes = await api.get('/activity?tab=all');
        setActivities(activityRes.data || []);
        setAllChatRows([]);
        await api.put('/activity/read');
      } catch {}
      setLoading(false);
    })();
  }, [tab]);

  const getIcon = (type) => {
    const Icon = ICONS[type] || Bell;
    const colors = {
      new_follower: 'text-[#B6E600]',
      like: 'text-[#FF3B30]',
      comment: 'text-[#6B6E73]',
      join_request: 'text-[#B6E600]',
      plan_joined: 'text-[#B6E600]',
      request_approved: 'text-[#B6E600]',
    };
    return <Icon size={16} className={colors[type] || 'text-[#9B9EA3]'} />;
  };

  const handleActivityClick = (act) => {
    // Comment / reply / like → deep-link with highlight so VibeDetail
    // can scroll-to + flash the exact comment and expand its thread.
    if ((act.type === 'comment' || act.type === 'comment_reply' || act.type === 'comment_like') && act.vibe_id) {
      const target = act.comment_id || '';
      navigate(`/vibe/${act.vibe_id}${target ? `?highlight=${target}` : ''}`);
      return;
    }
    if (act.type === 'like' && act.vibe_id) { navigate(`/vibe/${act.vibe_id}`); return; }
    if (act.type === 'story_reply' && act.thread_id) { navigate(`/dm/${act.thread_id}`); return; }
    if (act.type === 'story_mention' && act.actor?.id) { navigate(`/stories/${act.actor.id}`); return; }
    if (act.type === 'new_follower' || act.type === 'follow_request') {
      if (act.actor?.id) navigate(`/user/${act.actor.id}`);
      return;
    }
    if (act.type === 'chat_mention' && act.plan?.id) { navigate(`/plan/${act.plan.id}/chat`); return; }
    if (act.plan?.id) navigate(`/plan/${act.plan.id}`);
    else if (act.actor?.id) navigate(`/user/${act.actor.id}`);
  };

  // Notifications tab: just the grouped activity rows. (Chats moved to the
  // Messages tab — no more All/Inbox/Updates/Chats confusion.)
  const grouped = groupActivities(activities);
  const mergedAll = grouped.map(a => ({ _kind: 'activity', _ts: a._last || a.created_at, data: a }));

  return (
    <div className="content-area animate-fade-in" data-testid="activity-page">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-2xl font-bold tracking-tight text-[#111111]">Activity</h1>
      </div>

      {/* Stories rail — visible above the Notifications tab; Messages tab
          hides it so the chat list takes full attention. */}
      {tab === 'notifications' && <StoriesRail />}

      {/* Tabs — Notifications | Messages (Q10). */}
      <div className="px-4 mb-4 flex gap-2 overflow-x-auto hide-scrollbar" data-testid="activity-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`activity-tab-${t.testId}`}
            onClick={() => setTab(t.key)}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              tab === t.key
                ? 'bg-[#111111] text-white'
                : 'bg-[#F1F0EA] text-[#9B9EA3] border border-black/[0.06]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 pb-4">
        {tab === 'messages' ? (
          <ChatsList />
        ) : tab === 'vibes' ? (
          <FollowingVibesFeed />
        ) : loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : mergedAll.length === 0 ? (
          <div className="text-center py-16 px-6" data-testid="activity-empty-state">
            <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4">
              <Bell size={26} className="text-[#3F5200]" />
            </div>
            <p className="text-base font-extrabold text-[#111] mb-1">All caught up</p>
            <p className="text-sm text-[#6B6E73] leading-relaxed mb-4 max-w-xs mx-auto">
              When someone follows you, joins your plan, or replies to a story — it'll show up right here.
            </p>
            <button
              data-testid="activity-empty-cta"
              onClick={() => navigate('/explore')}
              className="px-5 py-2.5 rounded-full bg-[#B6E600] text-[#111] text-sm font-extrabold active:scale-95"
            >
              Find people to follow
            </button>
          </div>
        ) : (
          <div className="space-y-2 stagger-children">
            {mergedAll.map(entry => {
              // Chat unread rows — render as a chat-style card so DMs/groups/plan chats
              // truly surface under All (this fixes the original bug Fernando reported).
              if (entry._kind === 'chat') {
                const row = entry.data;
                return (
                  <button
                    key={`chat-${row.type}-${row.id}`}
                    data-testid={`activity-chat-${row.type}-${row.id}`}
                    onClick={() => navigate(row.nav_path)}
                    className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white border border-[#B6E600]/30 active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="flex-shrink-0 relative">
                      {row.avatar ? (
                        <img loading="lazy" decoding="async" src={row.avatar} alt="" className={`w-10 h-10 object-cover ${row.type === 'dm' ? 'rounded-full' : 'rounded-2xl'}`} />
                      ) : (
                        <div className={`w-10 h-10 bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3] ${row.type === 'dm' ? 'rounded-full' : 'rounded-2xl'}`}>
                          {row.type === 'plan' ? <ImageIcon size={16} /> : row.type === 'group' ? <Users size={16} /> : getInitials(row.title)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#111] leading-snug">
                        <span className="font-semibold">{row.title}</span>
                        <span className="text-[#6B6E73]"> · {row.type === 'dm' ? 'sent you a message' : row.type === 'group' ? 'new in group' : 'new in plan chat'}</span>
                      </p>
                      <p className="text-xs text-[#6B6E73] truncate mt-0.5">
                        {row.type === 'plan' && row.sender_name ? `${row.sender_name}: ` : ''}{row.last_message || '—'}
                      </p>
                      <span className="text-[11px] text-[#9B9EA3]">{timeAgo(row.last_message_at)}</span>
                    </div>
                    {row.unread_count > 0 && (
                      <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-bold flex items-center justify-center">
                        {row.unread_count > 99 ? '99+' : row.unread_count}
                      </span>
                    )}
                  </button>
                );
              }
              const act = entry.data;
              if (act.type === 'safety_warning' || act.type === 'safety_suspension') {
                return <SafetyCard key={act.id} act={act} />;
              }
              if (act.type === 'follow_request') {
                return (
                  <FollowRequestCard
                    key={act.id}
                    act={act}
                    onResolved={(followerId) => setActivities(prev => prev.filter(a => !(a.type === 'follow_request' && a.actor?.id === followerId)))}
                  />
                );
              }
              const groupedMsg = act._count > 1
                ? (act.type === 'comment'
                    ? `${act.actor?.display_name || 'Someone'} commented ${act._count} times on your vibe`
                    : `${act.actor?.display_name || 'Someone'} liked your vibe ${act._count} times`)
                : act.message;
              return (
                <div
                  key={act.id}
                  data-testid={`activity-${act.id}`}
                  className={`flex items-start gap-3 p-3 rounded-2xl border animate-fade-in cursor-pointer ${
                    act.read ? 'bg-[#FFFFFF] border-black/[0.06]' : 'bg-[#FFFFFF] border-[#B6E600]/30'
                  }`}
                  onClick={() => handleActivityClick(act)}
                >
                  <div className="flex-shrink-0">
                    {act.actor?.profile_photo ? (
                      <img loading="lazy" decoding="async" src={act.actor.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
                        {getInitials(act.actor?.display_name)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#2A2A2A] leading-snug">{groupedMsg}</p>
                    {act.preview && (
                      <p className="text-xs text-[#6B6E73] mt-0.5 line-clamp-2 italic">&ldquo;{act.preview}&rdquo;</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {getIcon(act.type)}
                      <span className="text-[11px] text-[#9B9EA3]">{timeAgo(act._last || act.created_at)}</span>
                      {act._count > 1 && (
                        <span className="text-[10px] font-bold text-[#3F5200] bg-[#B6E600]/15 px-1.5 py-0.5 rounded-full">×{act._count}</span>
                      )}
                    </div>
                    {/* Inline action chips — "what can I do next?" (Q13). */}
                    {act.type === 'new_follower' && act.actor?.id && (
                      <ActivityActionStrip act={act} testIdPrefix={`act-${act.id}`} />
                    )}
                    {(act.type === 'comment_reply' || act.type === 'comment') && act.vibe_id && (
                      <div className="flex gap-1.5 mt-2" onClick={e => e.stopPropagation()}>
                        <button
                          data-testid={`act-${act.id}-reply`}
                          onClick={() => navigate(`/vibe/${act.vibe_id}?highlight=${act.comment_id || ''}&reply=1`)}
                          className="text-[11px] font-extrabold text-[#111] bg-[#B6E600] px-3 py-1.5 rounded-full active:scale-95"
                        >
                          Reply
                        </button>
                        <button
                          data-testid={`act-${act.id}-view-thread`}
                          onClick={() => navigate(`/vibe/${act.vibe_id}?highlight=${act.comment_id || ''}`)}
                          className="text-[11px] font-extrabold text-[#111] bg-[#F1F0EA] px-3 py-1.5 rounded-full active:scale-95"
                        >
                          View thread
                        </button>
                      </div>
                    )}
                  </div>
                  {act.plan?.cover_image && (
                    <img loading="lazy" decoding="async" src={act.plan.cover_image} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
